import React, { useCallback, useEffect, useState } from 'react';
import { apiGet, apiPost } from '../../../utils/apiFetch';
import apiPath from '../../../utils/pathObj';
import useToastContext from 'hooks/useToastContext';
import { Btn, StatusBadge, fmtDate, BORDER, errMsg } from './adminUi';
import { ConfirmModal } from './AdminActionModals';

/** Payroll setup-fee payments: which company paid, when, and whether they have submitted the form yet. */
const AdminPayrollPayments = ({ onOpen }) => {
  const notification = useToastContext();
  const [data, setData] = useState({ rows: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [resetRow, setResetRow] = useState(null); // TESTING ONLY
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiGet(`${apiPath.payrollAdminBase}/payments`);
      if (res?.data?.success) setData(res.data.results);
      else notification.error(errMsg(res, 'Could not load payments'));
    } catch (e) {
      notification.error('Could not load payments');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  // TESTING ONLY – remove with the backend test-reset route
  const doReset = async () => {
    try {
      setBusy(true);
      const res = await apiPost(`${apiPath.payrollAdminBase}/test-reset/${resetRow.companyId}`, {});
      if (res?.data?.success) {
        notification.success('Payroll data removed for this company');
        setResetRow(null);
        load();
      } else notification.error(errMsg(res, 'Reset failed'));
    } catch (e) {
      notification.error('Reset failed');
    } finally {
      setBusy(false);
    }
  };

  const { rows, counts } = data;

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-6">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-700">Payroll Administration</p>
        <h1 className="text-2xl font-bold text-gray-900">Setup-fee payments</h1>
      </div>

      <div className="mb-6 grid grid-cols-3 gap-3">
        {[
          ['Paid', counts.paid || 0, 'text-green-700'],
          ['Paid – form not submitted', counts.awaitingForm || 0, 'text-amber-700'],
          ['Checkout started, unpaid', counts.pending || 0, 'text-gray-600'],
        ].map(([label, n, color]) => (
          <div key={label} className={`border ${BORDER} bg-white p-4`}>
            <p className={`text-3xl font-bold tabular-nums ${color}`}>{n}</p>
            <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-gray-500">{label}</p>
          </div>
        ))}
      </div>

      <div className={`overflow-x-auto border ${BORDER} bg-white`}>
        <table className="w-full min-w-[900px] text-left text-sm">
          <thead className="bg-[#e6e3de] text-xs font-bold uppercase tracking-wider text-gray-700">
            <tr>
              {['Company account', 'Amount', 'Payment', 'Date', 'Transaction', 'Form', ''].map((h) => (
                <th key={h} className="px-4 py-2.5">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400">No payments yet.</td></tr>}
            {!loading && rows.map((r) => (
              <tr key={r._id} className="border-t border-gray-100">
                <td className="px-4 py-3">
                  <p className="font-semibold text-gray-900">{r.companyName || '—'}</p>
                  <p className="text-xs text-gray-500">{r.companyEmail}</p>
                  {r.legalName && r.legalName !== r.companyName && <p className="text-xs text-gray-400">Legal: {r.legalName}</p>}
                </td>
                <td className="px-4 py-3 font-semibold tabular-nums text-gray-900">${Number(r.amount).toLocaleString()}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${r.status === 'paid' ? 'bg-green-50 text-green-800 ring-green-300' : 'bg-gray-100 text-gray-600 ring-gray-300'}`}>
                    {r.status === 'paid' ? 'Paid' : 'Unpaid'}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-700">{fmtDate(r.paidAt || r.createdAt)}</td>
                <td className="px-4 py-3 font-mono text-xs text-gray-500">{r.transactionId}</td>
                <td className="px-4 py-3">
                  {r.onboardingId ? (
                    <button type="button" onClick={() => onOpen(r.onboardingId)} className="inline-flex items-center gap-2 hover:underline">
                      <StatusBadge status={r.onboardingStatus} />
                    </button>
                  ) : r.status === 'paid' ? (
                    <span className="text-xs font-semibold text-amber-700">Not submitted yet</span>
                  ) : (
                    <span className="text-xs text-gray-400">—</span>
                  )}
                </td>
                  {/* TESTING ONLY */}
                {/* <td className="px-4 py-3 text-right">
                  <Btn className="!border-red-300 !px-2.5 !py-1 !text-xs !text-red-700 hover:!bg-red-50" onClick={() => setResetRow(r)}>Reset (test)</Btn>
                </td> */}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {resetRow && (
        <ConfirmModal
          title="Remove payroll data (testing only)"
          body={`This permanently deletes the payroll payment(s), onboarding form and Everee credentials of ${resetRow.companyName || 'this company'}. They will see the payment screen again. Uploaded files and Stripe records are not touched.`}
          confirmLabel="Delete payroll data"
          variant="danger"
          busy={busy}
          onConfirm={doReset}
          onClose={() => setResetRow(null)}
        />
      )}
    </div>
  );
};

export default AdminPayrollPayments;